// app/src/components/SuSuActionsPanel.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import { Connection, PublicKey } from "@solana/web3.js";

import { getActiveEndpoint, getActiveProgramId } from "../config/active";

import {
  PhantomProvider,
  createPoolV1,
  repairVaultV1,
  joinPoolV1,
  contributeV1,
  createPoolV2,
  joinPoolV2,
  contributeV2,
  executePayoutV2,
  advanceRoundV2,
} from "../solana/roundsClient";

import {
  derivePoolPdaV1,
  deriveVaultPdaV1,
  deriveMemberPdaV1,
  derivePoolPdaV2,
  deriveVaultPdaV2,
  deriveMemberPdaV2,
} from "../solana/weareSusuPdas";

function getPhantom(): PhantomProvider | null {
  const w = window as any;
  if (w?.phantom?.solana?.isPhantom) return w.phantom.solana as PhantomProvider;
  return null;
}

function short(pk?: string) {
  if (!pk) return "—";
  return `${pk.slice(0, 6)}…${pk.slice(-6)}`;
}

function asPublicKey(maybe: any): PublicKey | null {
  try {
    if (!maybe) return null;
    if (maybe instanceof PublicKey) return maybe;
    if (typeof maybe === "string") return new PublicKey(maybe);
    if (typeof maybe?.toBase58 === "function") return new PublicKey(maybe.toBase58());
    return new PublicKey(String(maybe));
  } catch {
    return null;
  }
}

function lamportsFromSolString(input: string): number | null {
  const s = input.trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  const lamports = Math.floor(n * 1_000_000_000);
  if (!Number.isSafeInteger(lamports) || lamports <= 0) return null;
  return lamports;
}

export default function SuSuActionsPanel() {
  const [phantom, setPhantom] = useState<PhantomProvider | null>(null);
  const [walletPk, setWalletPk] = useState<PublicKey | null>(null);

  const [lastAction, setLastAction] = useState("—");
  const [signature, setSignature] = useState("—");
  const [error, setError] = useState<string>("—");
  const [debug, setDebug] = useState<string[]>([]);

  const [contributeSolV1, setContributeSolV1] = useState<string>("0.001");
  const [contributeSolV2, setContributeSolV2] = useState<string>("0.001");

  // payout controls
  const [payoutRecipient, setPayoutRecipient] = useState<string>("");

  const pushLog = useCallback((line: string) => {
    setDebug((prev) => [...prev, `${new Date().toISOString()} ${line}`]);
  }, []);

  /**
   * BEST-PRACTICE PATCH:
   * - Do NOT freeze endpoint/programId at import-time or first render only.
   * - Sync from runtime-driven active config at mount AND next tick (handles late injection).
   * - Use the synced values for Connection + ProgramId so UI cannot drift to devnet.
   */
  const [active, setActive] = useState(() => {
    const ep = getActiveEndpoint();
    const pid = getActiveProgramId().toBase58();
    return { endpoint: ep, programIdBase58: pid };
  });

  useEffect(() => {
    const sync = () => {
      try {
        const ep = getActiveEndpoint();
        const pid = getActiveProgramId().toBase58();
        setActive((prev) =>
          prev.endpoint === ep && prev.programIdBase58 === pid
            ? prev
            : { endpoint: ep, programIdBase58: pid }
        );
      } catch (e: any) {
        // Don't hard-fail UI; log for auditability.
        pushLog(`active-config sync ERROR: ${e?.message ?? String(e)}`);
      }
    };

    sync();
    const t = window.setTimeout(sync, 0);
    return () => window.clearTimeout(t);
  }, [pushLog]);

  const endpoint = active.endpoint;
  const programId = useMemo(() => new PublicKey(active.programIdBase58), [active.programIdBase58]);
  const connection = useMemo(() => new Connection(endpoint, "confirmed"), [endpoint]);

  // ===== PDAs (V1)
  const poolPdaV1 = useMemo(() => {
    if (!walletPk) return null;
    return derivePoolPdaV1(programId, walletPk)[0];
  }, [walletPk, programId]);

  const vaultPdaV1 = useMemo(() => {
    if (!poolPdaV1) return null;
    return deriveVaultPdaV1(programId, poolPdaV1)[0];
  }, [poolPdaV1, programId]);

  const memberPdaV1 = useMemo(() => {
    if (!poolPdaV1 || !walletPk) return null;
    return deriveMemberPdaV1(programId, poolPdaV1, walletPk)[0];
  }, [programId, poolPdaV1, walletPk]);

  // ===== PDAs (V2)
  const poolPdaV2 = useMemo(() => {
    if (!walletPk) return null;
    return derivePoolPdaV2(programId, walletPk)[0];
  }, [walletPk, programId]);

  const vaultPdaV2 = useMemo(() => {
    if (!poolPdaV2) return null;
    return deriveVaultPdaV2(programId, poolPdaV2)[0];
  }, [poolPdaV2, programId]);

  const memberPdaV2 = useMemo(() => {
    if (!poolPdaV2 || !walletPk) return null;
    return deriveMemberPdaV2(programId, poolPdaV2, walletPk)[0];
  }, [programId, poolPdaV2, walletPk]);

  // Auto-detect Phantom + already-connected wallet on mount
  useEffect(() => {
    const p = getPhantom();
    pushLog(`mount: phantom detected=${!!p}`);
    if (!p) return;

    setPhantom(p);

    const pk = asPublicKey((p as any).publicKey);
    pushLog(`mount: phantom publicKey present=${!!pk}`);
    if (pk) {
      setWalletPk(pk);
      pushLog(`mount: wallet hydrated=${pk.toBase58()}`);
      setPayoutRecipient(pk.toBase58());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    pushLog(`state: phantom=${phantom ? "set" : "null"}`);
  }, [phantom, pushLog]);

  useEffect(() => {
    pushLog(`state: walletPk=${walletPk ? walletPk.toBase58() : "null"}`);
    if (walletPk) setPayoutRecipient(walletPk.toBase58());
  }, [walletPk, pushLog]);

  const connectWallet = useCallback(async () => {
    pushLog("connectWallet: invoked");
    try {
      setError("—");
      setSignature("—");
      setLastAction("Connect Phantom");

      const p = getPhantom();
      pushLog(`connectWallet: phantom detected=${!!p}`);
      if (!p) throw new Error("Phantom not installed");

      setPhantom(p);

      const existingPk = asPublicKey((p as any).publicKey);
      if (existingPk) {
        setWalletPk(existingPk);
        setPayoutRecipient(existingPk.toBase58());
        pushLog(`connectWallet: already connected=${existingPk.toBase58()}`);
        return;
      }

      pushLog("connectWallet: calling phantom.connect()");
      const res = await p.connect();

      const pk = asPublicKey((res as any)?.publicKey);
      if (!pk) throw new Error("Phantom connect returned no publicKey");

      setWalletPk(pk);
      setPayoutRecipient(pk.toBase58());
      pushLog(`connectWallet: connected=${pk.toBase58()}`);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`connectWallet: ERROR ${msg}`);
    }
  }, [pushLog]);

  const refresh = useCallback(async () => {
    pushLog("refresh: invoked");
    try {
      setError("—");
      setLastAction("Refresh");

      if (!walletPk) {
        pushLog("refresh: blocked (no walletPk)");
        return;
      }

      const [pp1] = derivePoolPdaV1(programId, walletPk);
      const [vp1] = deriveVaultPdaV1(programId, pp1);
      const [mp1] = deriveMemberPdaV1(programId, pp1, walletPk);

      const [pp2] = derivePoolPdaV2(programId, walletPk);
      const [vp2] = deriveVaultPdaV2(programId, pp2);
      const [mp2] = deriveMemberPdaV2(programId, pp2, walletPk);

      const [pool1, vault1, mem1, pool2, vault2, mem2] = await Promise.all([
        connection.getAccountInfo(pp1, "confirmed"),
        connection.getAccountInfo(vp1, "confirmed"),
        connection.getAccountInfo(mp1, "confirmed"),
        connection.getAccountInfo(pp2, "confirmed"),
        connection.getAccountInfo(vp2, "confirmed"),
        connection.getAccountInfo(mp2, "confirmed"),
      ]);

      pushLog("refresh: OK");
      pushLog(
        `Active: endpoint=${endpoint} program=${programId.toBase58()}`
      );
      pushLog(
        `V1 Pool: exists=${!!pool1} owner=${pool1?.owner?.toBase58?.() ?? "—"} dataLen=${pool1?.data?.length ?? 0}`
      );
      pushLog(
        `V1 Vault: exists=${!!vault1} owner=${vault1?.owner?.toBase58?.() ?? "—"} dataLen=${vault1?.data?.length ?? 0} lamports=${vault1?.lamports ?? 0}`
      );
      pushLog(
        `V1 Member: exists=${!!mem1} owner=${mem1?.owner?.toBase58?.() ?? "—"} dataLen=${mem1?.data?.length ?? 0}`
      );

      pushLog(
        `V2 Pool: exists=${!!pool2} owner=${pool2?.owner?.toBase58?.() ?? "—"} dataLen=${pool2?.data?.length ?? 0}`
      );
      pushLog(
        `V2 Vault: exists=${!!vault2} owner=${vault2?.owner?.toBase58?.() ?? "—"} dataLen=${vault2?.data?.length ?? 0} lamports=${vault2?.lamports ?? 0}`
      );
      pushLog(
        `V2 Member: exists=${!!mem2} owner=${mem2?.owner?.toBase58?.() ?? "—"} dataLen=${mem2?.data?.length ?? 0}`
      );

      pushLog("Functional Runbook (V2 single-member):");
      pushLog("1) Contribute (V2) a small amount (e.g., 0.001 SOL).");
      pushLog("2) Execute Payout (V2) to recipient (default = your wallet).");
      pushLog("3) Advance Round (V2) (should succeed only after payout).");
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`refresh: ERROR ${msg}`);
    }
  }, [walletPk, connection, programId, pushLog, endpoint]);

  // ===== V1 actions
  const doCreatePoolV1 = useCallback(async () => {
    pushLog("createPoolV1: invoked");
    if (!walletPk) return pushLog("createPoolV1: blocked (no walletPk)");
    if (!phantom) return pushLog("createPoolV1: blocked (no phantom)");

    try {
      setLastAction("CreatePool V1");
      setError("—");
      setSignature("—");

      const sig = await createPoolV1({
        connection,
        authority: walletPk,
        phantom,
        programId,
        onDebugLog: pushLog,
      });

      setSignature(sig);
      pushLog(`createPoolV1: SUCCESS ${sig}`);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`createPoolV1: ERROR ${msg}`);
    }
  }, [walletPk, phantom, connection, programId, pushLog]);

  const doRepairVaultV1 = useCallback(async () => {
    pushLog("repairVaultV1: invoked");
    if (!walletPk) return pushLog("repairVaultV1: blocked (no walletPk)");
    if (!phantom) return pushLog("repairVaultV1: blocked (no phantom)");

    try {
      setLastAction("RepairVault V1");
      setError("—");
      setSignature("—");

      const sig = await repairVaultV1({
        connection,
        authority: walletPk,
        phantom,
        programId,
        onDebugLog: pushLog,
      });

      setSignature(sig);
      pushLog(`repairVaultV1: SUCCESS ${sig}`);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`repairVaultV1: ERROR ${msg}`);
    }
  }, [walletPk, phantom, connection, programId, pushLog]);

  const doJoinPoolV1 = useCallback(async () => {
    pushLog("joinPoolV1: invoked");
    if (!walletPk) return pushLog("joinPoolV1: blocked (no walletPk)");
    if (!phantom) return pushLog("joinPoolV1: blocked (no phantom)");

    try {
      setLastAction("JoinPool V1");
      setError("—");
      setSignature("—");

      const sig = await joinPoolV1({
        connection,
        wallet: walletPk,
        phantom,
        programId,
        onDebugLog: pushLog,
      });

      setSignature(sig);
      pushLog(`joinPoolV1: SUCCESS ${sig}`);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`joinPoolV1: ERROR ${msg}`);
    }
  }, [walletPk, phantom, connection, programId, pushLog]);

  const doContributeV1 = useCallback(async () => {
    pushLog("contributeV1: invoked");
    if (!walletPk) return pushLog("contributeV1: blocked (no walletPk)");
    if (!phantom) return pushLog("contributeV1: blocked (no phantom)");

    const lamports = lamportsFromSolString(contributeSolV1);
    if (!lamports) {
      const msg = "Invalid V1 contribute amount (SOL). Example: 0.001";
      setError(msg);
      pushLog(`contributeV1: ERROR ${msg}`);
      return;
    }

    try {
      setLastAction("Contribute V1");
      setError("—");
      setSignature("—");

      const sig = await contributeV1({
        connection,
        wallet: walletPk,
        phantom,
        programId,
        lamports: BigInt(lamports),
        onDebugLog: pushLog,
      });

      setSignature(sig);
      pushLog(`contributeV1: SUCCESS ${sig}`);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`contributeV1: ERROR ${msg}`);
    }
  }, [walletPk, phantom, connection, programId, contributeSolV1, pushLog]);

  // ===== V2 actions
  const doCreatePoolV2 = useCallback(async () => {
    pushLog("createPoolV2: invoked");
    if (!walletPk) return pushLog("createPoolV2: blocked (no walletPk)");
    if (!phantom) return pushLog("createPoolV2: blocked (no phantom)");

    try {
      setLastAction("CreatePool V2");
      setError("—");
      setSignature("—");

      const sig = await createPoolV2({
        connection,
        authority: walletPk,
        phantom,
        programId,
        onDebugLog: pushLog,
      });

      setSignature(sig);
      pushLog(`createPoolV2: SUCCESS ${sig}`);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`createPoolV2: ERROR ${msg}`);
    }
  }, [walletPk, phantom, connection, programId, pushLog]);

  const doJoinPoolV2 = useCallback(async () => {
    pushLog("joinPoolV2: invoked");
    if (!walletPk) return pushLog("joinPoolV2: blocked (no walletPk)");
    if (!phantom) return pushLog("joinPoolV2: blocked (no phantom)");

    try {
      setLastAction("JoinPool V2");
      setError("—");
      setSignature("—");

      const sig = await joinPoolV2({
        connection,
        wallet: walletPk,
        phantom,
        programId,
        onDebugLog: pushLog,
      });

      setSignature(sig);
      pushLog(`joinPoolV2: SUCCESS ${sig}`);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`joinPoolV2: ERROR ${msg}`);
    }
  }, [walletPk, phantom, connection, programId, pushLog]);

  const doContributeV2 = useCallback(async () => {
    pushLog("contributeV2: invoked");
    if (!walletPk) return pushLog("contributeV2: blocked (no walletPk)");
    if (!phantom) return pushLog("contributeV2: blocked (no phantom)");

    const lamports = lamportsFromSolString(contributeSolV2);
    if (!lamports) {
      const msg = "Invalid V2 contribute amount (SOL). Example: 0.001";
      setError(msg);
      pushLog(`contributeV2: ERROR ${msg}`);
      return;
    }

    try {
      setLastAction("Contribute V2");
      setError("—");
      setSignature("—");

      const sig = await contributeV2({
        connection,
        wallet: walletPk,
        phantom,
        programId,
        lamports: BigInt(lamports),
        onDebugLog: pushLog,
      });

      setSignature(sig);
      pushLog(`contributeV2: SUCCESS ${sig}`);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`contributeV2: ERROR ${msg}`);
    }
  }, [walletPk, phantom, connection, programId, contributeSolV2, pushLog]);

  const doExecutePayoutV2 = useCallback(async () => {
    pushLog("executePayoutV2: invoked");
    if (!walletPk) return pushLog("executePayoutV2: blocked (no walletPk)");
    if (!phantom) return pushLog("executePayoutV2: blocked (no phantom)");

    const recipientPk = asPublicKey(payoutRecipient.trim());
    if (!recipientPk) {
      const msg = "Recipient pubkey is invalid";
      setError(msg);
      pushLog(`executePayoutV2: ERROR ${msg}`);
      return;
    }

    try {
      setLastAction("Execute Payout V2");
      setError("—");
      setSignature("—");

      pushLog(`Using RPC: ${endpoint}`);
      pushLog(`Program: ${programId.toBase58()}`);
      pushLog(`Signer: ${walletPk.toBase58()}`);
      pushLog(`Recipient: ${recipientPk.toBase58()}`);
      pushLog(`Pool PDA (V2): ${poolPdaV2?.toBase58() ?? "—"}`);
      pushLog(`Vault PDA (V2): ${vaultPdaV2?.toBase58() ?? "—"}`);

      const sig = await executePayoutV2({
        connection,
        signer: walletPk,
        phantom,
        programId,
        recipient: recipientPk,
        onDebugLog: pushLog,
      });

      setSignature(sig);
      pushLog(`executePayoutV2: SUCCESS ${sig}`);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`executePayoutV2: ERROR ${msg}`);
    }
  }, [walletPk, phantom, connection, programId, payoutRecipient, pushLog, poolPdaV2, vaultPdaV2, endpoint]);

  const doAdvanceRoundV2 = useCallback(async () => {
    pushLog("advanceRoundV2: invoked");
    if (!walletPk) return pushLog("advanceRoundV2: blocked (no walletPk)");
    if (!phantom) return pushLog("advanceRoundV2: blocked (no phantom)");

    try {
      setLastAction("Advance Round V2");
      setError("—");
      setSignature("—");

      const sig = await advanceRoundV2({
        connection,
        signer: walletPk,
        phantom,
        programId,
        onDebugLog: pushLog,
      });

      setSignature(sig);
      pushLog(`advanceRoundV2: SUCCESS ${sig}`);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setError(msg);
      pushLog(`advanceRoundV2: ERROR ${msg}`);
    }
  }, [walletPk, phantom, connection, programId, pushLog]);

  const canRefresh = !!walletPk;
  const canTx = !!walletPk && !!phantom;

  return (
    <div style={{ padding: 16 }}>
      <h3>SuSu Actions Panel</h3>

      <div>RPC: {endpoint}</div>
      <div>Program: {programId.toBase58()}</div>
      <div>Wallet: {walletPk ? short(walletPk.toBase58()) : "—"}</div>

      <br />

      <button onClick={connectWallet}>Connect Phantom</button>{" "}
      <button disabled={!canRefresh} onClick={refresh}>
        Refresh
      </button>

      <hr style={{ margin: "16px 0" }} />

      <h4>V1 (Legacy)</h4>
      <button disabled={!canTx} onClick={doCreatePoolV1}>
        Create Pool (V1)
      </button>{" "}
      <button disabled={!canTx} onClick={doRepairVaultV1}>
        Repair Vault (V1)
      </button>{" "}
      <button disabled={!canTx} onClick={doJoinPoolV1}>
        Join Pool (V1)
      </button>{" "}
      <input
        style={{ width: 120 }}
        value={contributeSolV1}
        onChange={(e) => setContributeSolV1(e.target.value)}
        placeholder="SOL"
      />{" "}
      <button disabled={!canTx} onClick={doContributeV1}>
        Contribute (V1)
      </button>

      <div style={{ marginTop: 8 }}>
        <div>Pool PDA (V1): {poolPdaV1?.toBase58() ?? "—"}</div>
        <div>Vault PDA (V1): {vaultPdaV1?.toBase58() ?? "—"}</div>
        <div>Member PDA (V1): {memberPdaV1?.toBase58() ?? "—"}</div>
      </div>

      <hr style={{ margin: "16px 0" }} />

      <h4>V2 (Canonical)</h4>
      <button disabled={!canTx} onClick={doCreatePoolV2}>
        Create Pool (V2)
      </button>{" "}
      <button disabled={!canTx} onClick={doJoinPoolV2}>
        Join Pool (V2)
      </button>{" "}
      <input
        style={{ width: 120 }}
        value={contributeSolV2}
        onChange={(e) => setContributeSolV2(e.target.value)}
        placeholder="SOL"
      />{" "}
      <button disabled={!canTx} onClick={doContributeV2}>
        Contribute (V2)
      </button>

      <div style={{ marginTop: 10, padding: 10, border: "1px solid #ddd" }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Payout (V2)</div>
        <div style={{ marginBottom: 6 }}>
          Recipient (must match current member wallet):{" "}
          <input
            style={{ width: 520 }}
            value={payoutRecipient}
            onChange={(e) => setPayoutRecipient(e.target.value)}
            placeholder="Recipient pubkey"
          />
        </div>
        <button disabled={!canTx} onClick={doExecutePayoutV2}>
          Execute Payout (V2)
        </button>{" "}
        <button disabled={!canTx} onClick={doAdvanceRoundV2}>
          Advance Round (V2)
        </button>
        <div style={{ marginTop: 8, fontSize: 12 }}>
          Policy: payout must occur before advance_round. With one member, round advances but current_index remains 0.
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <div>Pool PDA (V2): {poolPdaV2?.toBase58() ?? "—"}</div>
        <div>Vault PDA (V2): {vaultPdaV2?.toBase58() ?? "—"}</div>
        <div>Member PDA (V2): {memberPdaV2?.toBase58() ?? "—"}</div>
      </div>

      <hr style={{ margin: "16px 0" }} />

      <div>Last Action: {lastAction}</div>
      <div>Signature: {signature}</div>
      <div style={{ color: "red" }}>Error: {error}</div>

      <br />

      <div style={{ fontWeight: 700 }}>Debug Log</div>
      <pre style={{ whiteSpace: "pre-wrap", border: "1px solid #ccc", padding: 12 }}>
        {debug.length ? debug.join("\n") : "— (no logs yet) —"}
      </pre>
    </div>
  );
}
